import React, { FC, FormEvent, useCallback, useState } from 'react'
import classNames from 'classnames'
import { Button, LinkButton } from '@/atoms/Button'
import { TextArea } from '@/atoms/Input'
import { CaptionMD, CaptionSM, CaptionXS, Text } from '@/atoms/Text'
import { paths } from '@/constants'
import { Avatar } from '@/molecules/Avatar'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { useCreateWhyYouLoveThis } from '@/services/MomentsService'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { useTranslate } from '@/utils/translate/translate-client'

const UserAvatar: FC<{ className: string }> = ({ className }) => (
  <div className={classNames(className, 'absolute h-8 w-8 overflow-hidden rounded-full')}>
    <Avatar />
  </div>
)

export const AddANewStory: FC = () => {
  const [showStoryInput, setShowStoryInput] = useState(false)
  const [showStoryPrompt, setShowStoryPrompt] = useState(false)
  const [showLoginRequired, setShowLoginRequired] = useState(false)
  const { asset } = useAngelPlayerContext()
  const url = `${paths.getBaseURL()}${paths.watch.index}/${asset.projectSlug}/episode/${asset.sourceConfig.guid}`
  const { loginUrl, trackLoginStarted } = useLoginUrl(`${url}?completed=true`)
  const { t } = useTranslate('watch')
  const [text, setText] = useState('')
  const { engagementMomentDetails, projectSlug } = useCollectiblesPlayerEngagement()
  const track = useSafeTrack()
  const { isLoggedIn } = useUser()

  const moment = engagementMomentDetails?.moment

  const { createWhyYouLoveText, isSavingWhyYouLove } = useCreateWhyYouLoveThis()

  const submitStory = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!isSavingWhyYouLove) {
      try {
        const data = await createWhyYouLoveText({
          momentId: moment?.id,
          projectSlug: projectSlug,
          text: text,
        })
        if (data && data?.data?.createUserStoryText) {
          setShowStoryInput(false)
          track('Moment Story Created', {
            momentId: moment?.id,
            storyId: data.data.createUserStoryText.id,
          })
        }
      } catch (error) {
        const typedError = error as Error
        if (typedError.message === 'Forbidden resource') {
          setShowLoginRequired(true)
        }
      }
    }
  }

  const onCreateStoryClicked = useCallback(() => {
    if (isLoggedIn) {
      setShowStoryInput(true)
      track('Moment Story Creation Started', {
        momentId: moment?.id,
      })
    } else {
      setShowLoginRequired(true)
    }
  }, [isLoggedIn, setShowStoryInput, track, moment, setShowLoginRequired])

  return (
    <>
      {showStoryInput ? (
        <form onSubmit={submitStory} className="mb-5">
          <div className="relative flex">
            <TextArea
              value={text}
              onChange={(e) => setText(e.target.value)}
              containerClassName="w-full"
              className="h-32 rounded-xl bg-gray-900 py-3 pl-14"
            />
            <UserAvatar className="left-3 top-5" />
          </div>
          <CaptionXS
            className="m-1 cursor-pointer text-right underline"
            onClick={() => setShowStoryPrompt(!showStoryPrompt)}
          >
            {t('whatMakesAGreatStory', 'What makes a great story?')}
          </CaptionXS>
          {showStoryPrompt && (
            <CaptionMD className="m-1" weight="normal">
              {t(
                'whatMakesAGreatStoryDescription',
                'A great story is a short, personal reflection on why this scene is special to you. It can be a memory, an emotion, a thought, a dedication to someone important to you, or anything else that tells why this scene is meaningful.',
              )}
            </CaptionMD>
          )}
          <div className="m-2 mr-0 flex items-center justify-between">
            <CaptionSM>{t('storyMayBePublic', 'Your story may be shared publicly')}</CaptionSM>
            <div className="flex items-center">
              <CaptionSM className="mx-2 cursor-pointer" weight="bold" onClick={() => setShowStoryInput(false)}>
                {t('cancelStorySubmit', 'Cancel')}
              </CaptionSM>
              <Button variant="oxide-core">{t('post', 'POST')}</Button>
            </div>
          </div>
        </form>
      ) : (
        <div className="relative mb-5 flex h-12">
          <Text
            color="gray-400"
            className="photon-caption-lg lg:photon-caption-md xl:photon-caption-lg absolute left-0 flex h-12 w-full items-center rounded-full bg-gray-900 pl-12 text-gray-400"
            onClick={onCreateStoryClicked}
          >
            {t('tellUsWhyYouLoveThisScene', 'Tell us why you love this scene...')}
          </Text>
          <UserAvatar className="left-2 top-1/2 -translate-y-1/2" />
        </div>
      )}{' '}
      {showLoginRequired && (
        <div className="absolute left-1/2 top-0 z-10 flex h-full w-[90%] -translate-x-1/2 flex-col justify-center rounded-lg bg-gray-700 py-1">
          <CaptionMD className="my-2 self-center">
            {t('loginRequiredToCreateStory', 'Please login to share your story')}
          </CaptionMD>
          <div className="mt-1 flex justify-end pr-4">
            <Button variant="plain" onClick={() => setShowLoginRequired(false)}>
              {t('cancelStorySubmit', 'Cancel')}
            </Button>
            <LinkButton
              href={loginUrl}
              className="mx-3"
              variant="white"
              onClick={() => trackLoginStarted('collectible-scene-panel')}
            >
              {t('logIn', 'Log In')}
            </LinkButton>
          </div>
        </div>
      )}
    </>
  )
}
